// lib/UserContext.tsx
import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "./supabase";
import type { User } from "@supabase/supabase-js";

export interface UserProfile {
  id: string;
  email: string;
  full_name?: string | null;
  role: "admin" | "praticien";
  twilio_numero: string;
  rbac_enabled: boolean;
}

const UserContext = createContext<UserProfile | null | undefined>(undefined);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserProfile | null | undefined>(undefined);

  async function loadProfile(authUser: User) {
    const { data: profile, error } = await supabase
      .from("profiles")
      .select("id, email, role, twilio_numero")
      .eq("id", authUser.id)
      .single();

    if (error || !profile) {
      setUser({
        id: authUser.id,
        email: authUser.email ?? "",
        role: "praticien",
        twilio_numero: "",
        rbac_enabled: false,
      });
      return;
    }

    // Fetch rbac_enabled from clients table (clinic config)
    let rbac_enabled = false;
    if (profile.role === "praticien") {
      const { data: clientData } = await supabase
        .from("clients")
        .select("rbac_enabled")
        .eq("id", profile.id)
        .maybeSingle();
      rbac_enabled = clientData?.rbac_enabled ?? false;
    }

    setUser({ ...profile, rbac_enabled } as UserProfile);
  }

  useEffect(() => {
    let unsub: { unsubscribe: () => void } | undefined;

    // Récupère l’état initial SANS déclencher de refresh additionnel
    supabase.auth.getSession().then(({ data }) => {
      const authUser = data.session?.user;
      if (authUser) loadProfile(authUser);
      else setUser(null);
    });

    // Un seul listener, s’appuie sur la session fournie
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN") {
        if (session?.user) loadProfile(session.user);
      } else if (event === "SIGNED_OUT") {
        setUser(null);
      }
      // Pas de TOKEN_REFRESHED ici (on a désactivé l’auto-refresh)
    });

    unsub = listener?.subscription;

    return () => unsub?.unsubscribe();
  }, []);

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
